import type { TranslationResource } from './de';

export const en: TranslationResource = {
  common: {
    siteName: 'Die Sizilianer',
    siteTagline: 'Sicilian specialities in Dorsten',
    loading: 'Loading…',
    error: 'Error',
    save: 'Save',
    cancel: 'Cancel',
    submit: 'Send',
    delete: 'Delete',
    edit: 'Edit',
    close: 'Close',
    open: 'Open',
    back: 'Back',
    next: 'Next',
    yes: 'Yes',
    no: 'No',
  },

  nav: {
    home: 'Home',
    menu: 'Menu',
    reservation: 'Reservation',
    events: 'Events',
    gallery: 'Gallery',
    about: 'About us',
    contact: 'Contact',
    impressum: 'Imprint',
    privacy: 'Privacy',
    terms: 'Terms',
    newsletter: 'Newsletter',
    laputia: 'La Putia',
  },

  language: {
    label: 'Language',
    de: 'Deutsch',
    it: 'Italiano',
    en: 'English',
  },

  menu: {
    eyebrow: 'Buon appetito',
    title: 'Menu',
    lede: 'Authentic Sicilian cuisine — freshly prepared, served with love. All prices in Euro incl. VAT.',
    searchPlaceholder: 'Search dish…',
    diet: {
      vegetarian: 'Vegetarian',
      vegan: 'Vegan',
      glutenFree: 'Gluten-free',
      lactoseFree: 'Lactose-free',
      special: 'Today',
      seasonal: 'Seasonal',
      seasonalUntil: 'Seasonal until {{date}}',
    },
    filterMatches: '{{count}} results',
    filterReset: 'Reset filters',
    empty: {
      title: 'No dishes available',
      description: 'Currently no dishes are available. Please check back later.',
      filteredTitle: 'No matching dishes',
      filteredDescription: 'No dishes match the selected filters. Reset the filters to see the full menu.',
    },
    allergens: 'Allergens',
    price: 'Price',
    tableContext: {
      tavolo: 'Tavolo',
      table: 'Table',
      callService: 'Call service',
      hint: 'Browse our menu. Orders are taken directly by staff.',
    },
  },

  home: {
    pageTitle: 'Sicilian Restaurant',
    pageDescription: 'Authentic Sicilian cuisine in the heart of Dorsten.',
    hero: {
      imageAlt: 'Sicilian scene with Trinacria figure, Mount Etna, ancient temple and prickly pear cactus',
      kicker: 'Cucina siciliana autentica',
      title: 'DIE SIZILIANER',
      ledeLine1: 'Authentic recipes. The finest ingredients.',
      ledeLine2: 'A piece of Sicily. In the heart of Dorsten.',
      reserveCta: 'Reserve a table',
    },
    trustLine: '4.6 out of 5 on Google · over 1,700 reviews →',
    kueche: {
      eyebrow: 'Our kitchen',
      title: 'A feast for the senses',
      sub: 'Sicilian flavours, traditionally prepared. Served with love.',
      cards: {
        antipasti: 'Antipasti',
        antipastiSub: 'The perfect start',
        pasta: 'Pasta',
        pastaSub: 'Tradition & passion',
        pesce: 'Pesce',
        pesceSub: 'Fresh from the sea',
        dolci: 'Dolci',
        dolciSub: 'Sweet temptation',
      },
    },
    events: {
      eyebrow: 'Events',
      title: 'Food. Music. Moments.',
      text: 'Experience special evenings with Sicilian cuisine, live music and true hospitality.',
      cta: 'See all events',
      loading: 'Loading…',
      emptyTitle: 'No events scheduled right now',
      emptyText: 'We\'re planning our next events. Check back soon or book us for your own event.',
      emptyCta: 'Book the Ape',
      fallbackTitle: 'Ape event',
      more: 'Learn more',
    },
    laPutiaTeaser: {
      badge: 'New: our shop',
      title: 'La Putia',
      imageAlt: 'La Putia',
      taglineFallback: 'Sicilian bottega in Dorsten',
      text: 'Sicilian specialities to take away — cheese, salumi, olive oil from Agrigento, arancini and Sicilian panini. Right in Dorsten town centre.',
      ctaOpen: 'Discover La Putia',
      ctaClosed: 'Coming soon — learn more',
    },
  },

  galerie: {
    title: 'Gallery',
    scriptLine: 'Impressioni',
    lede: 'Sicily in pictures — illustrations of the island, its colours and its cuisine that inspire us.',
    instagram: 'For more impressions, follow us on Instagram',
    captions: {
      sizilien: 'Sicily',
      antipasti: 'Antipasti',
      pasta: 'Pasta',
      trinacria: 'Trinacria',
      pesce: 'Pesce',
      cannoli: 'Cannoli',
      ficoDIndia: "Fico d'India",
      olivo: 'Olivo',
      piazza: 'Piazza',
      limoni: 'Limoni',
      pomodoro: 'Pomodoro',
      cucina: 'Cucina',
    },
  },

  laPutia: {
    pageTitle: 'La Putia',
    pageDescription: 'La Putia — Sicilian bottega in Dorsten town centre. Fine foods, street food and specialities to take away. From Die Sizilianer.',
    storeDescriptionFallback: 'Sicilian bottega in Dorsten town centre',
  },

  ape: {
    shared: {
      toHome: 'Back to home',
      toApeBookings: 'To the Ape bookings',
      loadingBooking: 'Loading booking…',
      bookingNotFound: 'Booking not found',
      bookingLoadError: 'The booking could not be loaded.',
      noTokenTitle: 'No token',
      unknownError: 'Unknown error',
      errorPrefix: 'Error: {{message}}',
      flatPrice: 'Flat price',
      statusConfirmed: 'Confirmed',
      statusCancelled: 'Cancelled',
      timeAt: '{{time}}',
    },
    requestReceived: {
      pageTitle: 'Request received',
      scriptLine: 'Grazie mille',
      title: 'Request received',
      body: 'We\'ve received your request and will call you within 48 hours to discuss the details and prepare a binding offer.',
      whatNow: 'What happens next?',
      step1: '<strong>1.</strong> We check availability and give you a call',
      step2: '<strong>2.</strong> We discuss the menu, number of guests and schedule',
      step3: '<strong>3.</strong> You receive a written offer by e-mail',
      step4: '<strong>4.</strong> You accept with one click — done',
      callNow: 'Call now',
    },
    bookingConfirmed: {
      pageTitle: 'Booking confirmed',
      noTokenText: 'This page can only be opened via the link in your confirmation e-mail.',
      scriptCancelled: 'What a pity',
      scriptConfirmed: 'We\'re delighted',
      titleCancelled: 'Booking cancelled',
      titleConfirmed: 'Booking confirmed',
      introCancelled: 'This booking has been cancelled. We hope for another occasion.',
      introConfirmed: 'Your Ape booking is binding. We look forward to the event!',
      eventFallback: 'Event',
      statusBinding: 'Binding',
      cancelCta: 'Cancel',
      mailNote: 'You\'ll also receive a confirmation e-mail with all the details.',
    },
    cancel: {
      pageTitle: 'Cancel booking',
      noTokenText: 'This page can only be opened via the cancellation link in your confirmation e-mail.',
      toastAlready: 'The booking was already cancelled.',
      toastCancelled: 'The booking has been cancelled.',
      scriptLine: 'Until next time',
      cancelledTitle: 'Booking cancelled',
      cancelledBody: 'Your booking on {{date}} has been cancelled. We hope to see you again on another occasion.',
      newRequest: 'New request',
      confirmTitle: 'Cancel booking?',
      confirmText: 'This booking will be cancelled and the date released again. This action cannot be undone.',
      bookingFallback: 'Booking',
      name: 'Name',
      date: 'Date',
      time: 'Time',
      location: 'Location',
      reasonLabel: 'Reason (optional)',
      reasonHint: 'Helps us improve our service',
      reasonPlaceholder: 'e.g. the date has changed',
      keepButton: 'Keep it after all',
      cancelButton: 'Cancel booking',
    },
    quote: {
      pageTitle: 'Your offer',
      noTokenTitle: 'No token provided',
      noTokenText: 'This page can only be opened via the link in the offer e-mail.',
      toastAlready: 'The booking was already confirmed.',
      toastAccepted: 'Booking confirmed as binding!',
      loading: 'Loading offer…',
      notFound: 'Offer not found',
      loadError: 'The offer could not be loaded.',
      scriptLine: 'Your offer',
      cancelledTitle: 'Booking has already been cancelled',
      cancelledText: 'If you\'d like to reactivate the event, give us a call.',
      confirmedTitle: 'Booking already confirmed',
      confirmedText: 'We look forward to seeing you!',
      keyFacts: 'Key details',
      statusPleaseConfirm: 'Please confirm',
      guestsApprox: 'approx. {{guests}} guests',
      notes: 'Notes',
      acceptHint: 'If the offer works for you, simply accept it here as binding. We\'ll send you a confirmation e-mail right away.',
      acceptCta: 'Accept offer as binding',
      phoneHint: 'Prefer to talk it through by phone?',
    },
  },

  reservation: {
    title: 'Reserve a table',
    eyebrow: 'Reservation',
    scriptLine: 'Riserva il tuo tavolo',
    intro: 'Choose date, time and number of guests — we confirm reservations up to 6 people instantly.',
    // Reservierungsstopp (0089)
    stop: {
      title: 'Online reservations paused',
      body: 'Due to very high demand we are unable to accept new reservations at the moment. Please try again later.',
      dayBody: 'Unfortunately we are not taking online reservations for this day. Please choose another day or give us a call.',
    },
    step1: {
      guestsLabel: 'How many people?',
      guestsHint: 'Up to 6 guests confirmed instantly, 7–12 on request. For more than 12, please call us directly:',
      dateLabel: 'Which day?',
      timeLabel: 'What time?',
      continue: 'Next',
    },
    step2: {
      title: 'Contact details',
      name: 'Name',
      email: 'E-mail',
      phone: 'Phone',
      notes: 'Notes (optional)',
      notesHint: 'Allergies, table preference, occasion',
      birthday: 'We\'re celebrating a birthday',
      birthdayDate: 'Birthday date',
      birthdayHint: 'So we know who\'s celebrating',
      areaPreference: 'Area preference (optional)',
      areaInside: 'Inside',
      areaTerrace: 'Terrace',
      areaNoPreference: 'No preference',
      back: 'Back',
      continue: 'Next to summary',
    },
    step3: {
      title: 'Summary',
      lede: 'Please review your details.',
      submit: 'Confirm reservation',
      submitting: 'Sending…',
    },
    slotPicker: {
      loading: 'Loading availability…',
      emptyTitle: 'No available times',
      emptyDefault: 'No slots are available for this day.',
      groupLabel: 'Select a time',
      seatsFree: '{{count}} free',
    },
    guestsPicker: {
      groupLabel: 'Number of guests',
      decrease: 'One person fewer',
      increase: 'One more person',
      person: 'Person',
      persons: 'People',
    },
    datePicker: {
      groupLabel: 'Select a date',
      today: 'Today',
    },
    availability: {
      title: 'Live availability',
      today: 'Today',
      full: 'Full',
      free: 'free',
    },
    waitlistForm: {
      errorName: 'Please enter your full name',
      errorEmail: 'Valid e-mail required',
      errorPhone: 'Phone number required',
      joinFailed: 'Could not join the waitlist',
      successTitle: 'You\'re on the waitlist!',
      successPosition: 'You\'re at <strong>position {{position}}</strong> for {{date}} at {{time}}.',
      successNote: 'We\'ll e-mail you as soon as a table becomes free.',
      title: 'Join the waitlist',
      summaryOne: '{{date}} · {{time}} · {{guests}} person',
      summaryMany: '{{date}} · {{time}} · {{guests}} people',
      note: 'We\'ll write to you right away as soon as a table becomes free (link valid for 24 h).',
      namePlaceholder: 'First and last name',
      emailPlaceholder: 'E-mail',
      phonePlaceholder: 'Phone',
      notesPlaceholder: 'Notes (optional)',
      submit: 'Join waitlist',
      cancel: 'Cancel',
    },
    calendar: {
      addToCalendar: 'Add to calendar',
      google: 'Google Calendar',
      googleHint: 'Opens in your browser',
      apple: 'Apple / Outlook (.ics)',
      appleHint: 'Download and double-click',
      outlook: 'Outlook.com',
      outlookHint: 'Microsoft Web',
      eventTitle: 'Reservation — Die Sizilianer',
      manageHint: 'View, change or cancel your reservation (link valid until the day of your reservation):',
      eventDescriptionOne: '{{guests}} person · Reservation under the name {{name}}.',
      eventDescriptionMany: '{{guests}} people · Reservation under the name {{name}}.',
      addressLine: 'Die Sizilianer · Im Werth 25 · 46282 Dorsten · Tel. 02362 23279',
    },
    shared: {
      loading: 'Loading reservation…',
      notFound: 'Reservation not found',
      cardTitle: 'Reservation',
      name: 'Name',
      date: 'Date',
      time: 'Time',
      guests: 'Guests',
      area: 'Area',
      notes: 'Notes',
      email: 'E-mail',
      timeAt: '{{time}}',
      areaInside: 'Inside',
      areaTerrace: 'Terrace',
      addressLine: 'Im Werth 25, 46282 Dorsten',
      route: 'Directions →',
      questionsPhone: 'Questions? Call',
      newReservation: 'Book again',
      toHome: 'Back to home',
      contactUs: 'Contact us',
      noTokenTitle: 'No reservation token',
      unknownError: 'Unknown error',
      networkError: 'Network error',
      status: {
        cancelled: 'Cancelled',
        pending: 'Being reviewed',
        confirmed: 'Confirmed',
        completed: 'Completed',
        noShow: 'No-show',
      },
    },
    manage: {
      pageTitle: 'Manage reservation',
      pageDescription: 'View, change or cancel your reservation at Die Sizilianer in Dorsten',
      scriptLine: 'Benvenuti',
      title: 'Your reservation',
      introOpen: 'Here you can view, change or cancel your reservation up until the day of your visit.',
      introInactive: 'This reservation is no longer active.',
      editButton: 'Change reservation',
      cancelButton: 'Cancel reservation',
      modifyLockedCancel: 'Changes are possible up to 2 hours before your reservation. For short-notice changes, please give us a call.',
      modifyLockedNoCancel: 'Changes are possible up to 2 hours before your reservation. For anything else, please give us a call.',
      edit: {
        scriptLine: 'Andiamo',
        title: 'Change reservation',
        previouslyOne: 'Currently: {{date}}, {{time}}, {{guests}} person',
        previouslyMany: 'Currently: {{date}}, {{time}}, {{guests}} people',
        guestsLabel: 'Number of guests',
        dateLabel: 'Date',
        timeLabel: 'Time',
        notesLabel: 'Notes (optional)',
        areaLabel: 'Seating area',
        hintLargeGroup: 'For more than 6 guests we\'ll review the change manually once more.',
        hintCallUs: 'For more than 12 guests, please call us:',
        notesPlaceholder: 'e.g. allergies, high chair, occasion …',
        areaNoPreference: 'No preference',
        back: 'Back',
        save: 'Save changes',
        saving: 'Saving…',
        closedDay: '{{weekday}} is our day off.',
        pastDate: 'Please choose a future date.',
        closedSpecialDay: 'We are closed on this day.',
        noSlots: 'No free slots are available for this day.',
        toastNoChange: 'No changes — everything stays as it is.',
        toastPending: 'Change saved! As your group now has more than 6 people, we\'ll review the reservation once more and get back to you.',
        toastSaved: 'Change saved! You\'ll receive a confirmation by e-mail.',
      },
      noTokenText: 'This page can only be opened via the management link in your reservation e-mail.',
      expiredTitle: 'This link has expired',
      expiredText: 'The management link is valid until the end of your reservation day. We look forward to welcoming you back soon!',
    },
    confirmed: {
      pageTitle: 'Reservation',
      pageDescription: 'Your reservation at Die Sizilianer in Dorsten',
      scriptLine: 'Thank you',
      titleCancelled: 'Reservation cancelled',
      titlePending: 'Request received',
      titleConfirmed: 'Reservation confirmed',
      introCancelled: 'This reservation has been cancelled.',
      introPending: 'We are reviewing your request and will confirm it within 24 hours.',
      introConfirmed: 'We look forward to your visit!',
      manageHint: 'Via the management link in your confirmation e-mail you can change or cancel your reservation at any time, <strong>up until the day of your visit</strong>.',
      manageButton: 'Manage reservation',
      cancelButton: 'Cancel reservation',
      noTokenText: 'This page can only be opened via the link in your confirmation e-mail.',
    },
    cancelPage: {
      pageTitle: 'Cancel reservation',
      pageDescription: 'Cancellation of your reservation at Die Sizilianer in Dorsten',
      toastAlreadyCancelled: 'The reservation was already cancelled.',
      toastCancelled: 'The reservation has been cancelled.',
      errorPrefix: 'Error: {{message}}',
      noLinkTitle: 'No cancellation link',
      noLinkText: 'This page can only be opened via the cancellation link in your confirmation e-mail.',
      loadError: 'This reservation could not be loaded.',
      scriptLine: 'Until next time',
      cancelledTitle: 'Reservation cancelled',
      cancelledBody: 'Your reservation on <strong>{{date}}</strong> at <strong>{{time}}</strong> has been cancelled. We hope to welcome you back soon.',
      confirmTitle: 'Cancel reservation?',
      confirmText: 'This reservation will be cancelled and the table released again. This action cannot be undone.',
      reasonLabel: 'Reason (optional)',
      reasonHint: 'Helps us improve our service',
      reasonPlaceholder: 'e.g. plans have changed',
      keepButton: 'Keep it after all',
      cancelButton: 'Cancel reservation',
    },
    waitlistClaim: {
      pageTitle: 'Waitlist · Claim your table',
      pageDescription: 'Claim your table from the waitlist.',
      noToken: 'No token provided.',
      genericError: 'Error',
      loading: 'Loading…',
      expiredTitle: 'Link expired',
      expiredText: 'The reservation link was only valid for 24 hours. The table has since been offered to the next person on the waitlist.',
      expiredCta: 'Book a new table',
      claimedTitle: 'Already booked',
      claimedText: 'You have already turned this spot into a confirmed reservation. A confirmation e-mail is in your inbox.',
      successScript: 'Perfetto!',
      successTitle: 'Reservation confirmed',
      successText: 'We look forward to seeing you. The confirmation is in your inbox.',
      viewMenu: 'View menu',
      errorTitle: 'Something went wrong',
      readyScript: 'Buon ritorno!',
      readyTitle: 'A table is free',
      readyText: 'A table has become available — you can now make your reservation binding with a single click.',
      guestsLine: '{{guests}} people',
      validity: 'Valid for 24 h',
      claiming: 'Booking…',
      claimCta: 'Claim your table',
    },
  },

  footer: {
    address: 'Address',
    phone: 'Reservation',
    hours: 'Opening hours',
    follow: 'Follow us',
    newsletter: {
      title: 'Newsletter',
      lede: 'Specials, seasonal dishes and stories — <strong>1× per month</strong>.',
    },
  },

  events: {
    title: 'Events & Ape Booking',
    eyebrow: 'Events',
    scriptLine: 'Our Ape',
    lede: 'We bring our stone oven to your event — wedding, city festival, company party. Freshly baked pizza on the spot.',
    upcoming: 'Where to find us',
    upcomingEmpty: 'No public events scheduled — write to us for a private booking.',
    publicEmptyTitle: 'No public appearances scheduled right now',
    publicEmptyDescription: 'We\'re always planning new events — check back soon or follow us on Instagram.',
    requestCta: 'Send request',
    checkAvailability: 'Check availability',
    detailsLink: 'View details',
    detailBack: 'All events',
    detailFallbackTitle: 'Public event',
    calculator: {
      button: 'Calculate estimate',
      calculating: 'Calculating…',
      title: 'Non-binding instant estimate',
      persons: 'guests',
      travelIncluded: 'Travel: included',
      travelOneWay: 'Travel',
      travelRoundTrip: 'Round trip',
      disclaimer:
        'Non-binding estimate — your binding quote arrives by e-mail after a personal call.',
      osm: 'Distance: © OpenStreetMap contributors',
      disabled:
        'The instant estimate is currently unavailable — just send the request and we\'ll get back to you with a quote.',
      belowMin:
        'For smaller groups we\'re happy to prepare an individual quote — just send the request.',
      beyondMax:
        'The venue is outside our standard area — we\'ll review your request manually and get back to you.',
      geocodeFailed:
        'We couldn\'t locate the venue — we\'ll review your request manually and get back to you.',
      imprecise:
        'We can\'t determine the distance precisely enough for this address — adding street and number usually helps. Otherwise just send the request and we\'ll work it out by hand.',
    },
    requestForm: {
      title: 'Book the Ape',
      lede: 'Send us your details — we\'ll reply with a personal quote within 24h.',
      date: 'Date',
      eventType: 'Occasion',
      startTime: 'Time',
      endTime: 'until (optional)',
      expectedGuests: 'Guests (approx.)',
      salutationOptions: { herr: 'Mr', frau: 'Ms', keine: 'Prefer not to say' },
      titleOptions: { dr: 'Dr.', prof: 'Prof.', prof_dr: 'Prof. Dr.' },
      locationHeading: 'Where does it take place?',
      locationName: 'Name of the venue',
      locationNameHint: 'e.g. "Schützenhalle Lembeck" — optional',
      locationAddress: 'Address',
      locationStreet: 'Street & number (optional)',
      locationStreetHint: 'Optional — makes the distance estimate more precise',
      locationZip: 'Postcode',
      locationCity: 'Town',
      phoneHint: 'For phone queries about your request',
      isPublic: 'Public event — may appear on our website',
      message: 'Message',
      salutation: 'Salutation',
      academicTitle: 'Academic title',
      academicTitleNone: 'no title',
      firstName: 'First name',
      lastName: 'Surname',
      name: 'Name',
      email: 'E-mail',
      phone: 'Phone',
      submit: 'Send request',
      sending: 'Sending…',
    },
    priceMode: {
      pauschal: 'Flat price',
      pizzaAb: 'from',
      keinPreis: 'Price on request',
    },
  },

  newsletter: {
    title: 'Newsletter',
    scriptLine: 'Stay in touch',
    lede: 'Seasonal specials, Sicilian wine recommendations and little stories from the kitchen. 1× a month — no ads, no spam.',
    benefits: {
      specials: {
        title: 'Seasonal specials first',
        text: 'Be the first to know about new dishes.',
      },
      frequency: {
        title: 'Maximum 1× per month',
        text: 'No flood — promised.',
      },
    },
    namePlaceholder: 'Your first name',
    emailPlaceholder: 'your@email.com',
    submit: 'Subscribe',
    consent: 'By signing up you agree to receive emails at the address provided. Unsubscribe any time via the link in every message.',
    successPending: 'Almost there — please check your inbox.',
    confirmed: {
      title: 'Subscription confirmed',
      scriptLine: 'Thank you!',
      lede: 'We\'re happy to have you on our list. You\'ll hear from us soon.',
      alreadyTitle: 'You\'re already subscribed',
    },
    unsubscribed: {
      title: 'Unsubscribe confirmed',
      lede: 'You won\'t receive any more newsletters from us. Sorry to see you go — but we respect your choice.',
    },
    disabled: {
      title: 'The newsletter is taking a break',
      lede: 'We\'re not accepting new sign-ups at the moment. Check back soon — or simply visit us at the restaurant.',
    },
  },

  contact: {
    title: 'Contact & Reservation',
    eyebrow: 'Contact',
    scriptLine: 'Welcome',
    lede: 'We look forward to seeing you. Book online or call us — reservations are especially recommended on weekends.',
    info: {
      title: 'How to reach us',
      address: 'Address',
      phone: 'Phone',
      email: 'E-mail',
      hours: 'Opening hours',
      social: 'Follow us',
    },
    form: {
      title: 'Write to us',
      lede: 'We confirm your request within 24 hours.',
      name: 'Name',
      email: 'E-mail',
      subject: 'Subject',
      subjectOptions: {
        general: 'General',
        reservation: 'Reservation',
        event: 'Event request',
        catering: 'Catering',
        feedback: 'Feedback',
        other: 'Other',
      },
      message: 'Notes',
      messagePlaceholder: 'Allergies, special requests, occasion …',
      consent: 'I agree that my data is processed according to the privacy policy.',
      submit: 'Send message',
      submitting: 'Sending …',
      successTitle: 'Thank you',
      successText: 'Thank you! Your message has arrived. We\'ll reply within 24 hours.',
      newMessage: 'New message',
      preferOnline: 'Prefer to book directly?',
      reserveLink: 'Online reservation →',
    },
    map: {
      address: 'Im Werth 25 · 46282 Dorsten',
      openLarger: 'Open larger map →',
    },
    faq: {
      eyebrow: 'Frequently asked',
      title: 'Maybe we have the answer',
    },
  },

  notFound: {
    scriptLine: 'I\'m sorry',
    title: '404',
    lede: 'The page you\'re looking for doesn\'t exist or has been moved.',
    backHome: 'Back to home',
  },

  aboutUs: {
    title: 'About us',
    scriptLine: 'Our story',
    lede: 'Sicily has two homes — the island in the Mediterranean and our restaurant in Dorsten. One family, two roots, one passion.',
    section1: {
      handwritten: 'Salvatore "Toto"',
      title: 'From Sciacca to Dorsten',
      p1: 'Salvatore — known to everyone simply as "Toto" — comes from Sciacca on the south-west coast of Sicily, a fishing town where the sea sets the rhythm of the day. From there he brought the cuisine of his homeland with him: honest, warm, Sicilian.',
      p2: 'What started as love for one\'s own homeland is today "Die Sizilianer" — a restaurant that brings Sicily to Dorsten. With grandmothers\' recipes, with ingredients directly from the island, and with the conviction that good food must be shared.',
    },
    section2: {
      title: 'A kitchen between sea and volcano',
      p1: 'Sicilian cuisine is not just a variant of Italian — it is a world of its own. Shaped by Greeks, Arabs, Spaniards and Normans. Sweet and salty in one dish. Fish and land. Pistachio and lemon.',
      p2: 'We don\'t invent anything new. We cook the way one cooks at home. Pasta alla Norma. Caponata. Cannoli with fresh ricotta. Pizza Vastasa Catania-style. And truffle pasta — because even classic recipes deserve a touch of luxury.',
      motto: '„Mangiare insieme è vivere insieme."',
    },
    values: {
      first: {
        title: 'Fresh ingredients',
        description: 'Pistachios from Bronte, tomatoes from Pachino, olive oil from Castelvetrano — directly from the island.',
      },
      second: {
        title: 'Homemade',
        description: 'Pasta, sauces, bread and desserts are made fresh every morning in our kitchen.',
      },
      third: {
        title: 'Family',
        description: 'You\'re not sitting in a restaurant with us — you\'re sitting at the table of a Sicilian family.',
      },
    },
  },
  tafel: {
    pageTitle: 'Order board',
    pageDescription: 'Which pizza is ready? The order board at the Sizilianer pizza van.',
    heading: 'Order board',
    scriptLine: 'Pronto!',
    chooseSale: 'Which event are you at?',
    noSale: 'No sale is running right now. The board fills up as soon as the first order is written at the van.',
    unknownSale: 'This board does not exist — or the sale has already been closed.',
    ready: 'Ready for pickup',
    inProgress: 'In progress',
    nothingReady: 'Nothing ready yet.',
    nothingInProgress: 'Nothing in the oven right now.',
    asOf: 'As of {{zeit}}',
    waiting: 'for {{dauer}}',
    loading: 'Loading the board…',
    loadError: 'The board could not be loaded. It will try again in a few seconds by itself.',
    disabled: 'The order board is currently unavailable.',
    privacyNote: 'Only the order number and — if given — the first name are shown. No amounts, no order contents.',
    backToSales: 'Pick a different event',
  },
  chatbot: {
    launcherLabel: 'Open chat',
    consent: {
      title: 'Start a chat with us?',
      body: 'You are chatting with an AI, not a human. Our chat assistant is provided by Baroon AI. When it loads, your IP address is shared with Baroon AI. Your messages are relayed through our own server; at times we store conversations there for a limited period (30 days) to check answer quality — as are the pages you visit on our site. We do not track you beyond this website.',
      privacy: 'More in our privacy policy',
      accept: 'Start chat',
      decline: 'Not now',
      error: 'The chat could not be loaded. Please try again later.',
      errorTitle: 'Chat unavailable',
    },
    footerRevoke: 'Withdraw chat consent',
  },
};
